/**
 * Mutations for the org's manually tracked KPIs (`kpi_definitions` and
 * `kpi_snapshots`, migration 12). Reads come through the `kpi_tracked`
 * dataset so the dashboard and the manager dialog share one cache entry.
 *
 * Values are stored in the metric's *natural* unit — cents for currency,
 * a 0..1 ratio for percent — matching every other source in the catalog.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import type { Role } from '@/lib/permissions';

export const TRACKED_KPI_UNIT_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'currency', label: 'Currency ($)' },
  { id: 'percent', label: 'Percent (%)' },
  { id: 'count', label: 'Count' },
  { id: 'days', label: 'Days' },
  { id: 'months', label: 'Months' },
  { id: 'ratio', label: 'Ratio' },
  { id: 'multiple', label: 'Multiple (×)' },
  { id: 'score', label: 'Score' },
];

export interface KpiDefinitionInput {
  key: string;
  label: string;
  unit: string;
  direction: 'higher_is_better' | 'lower_is_better';
  target_value: number | null;
  visible_to_roles: Role[];
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({ queryKey: ['intel-dash', 'kpi_tracked'] });
}

export function slugifyKpiKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
}

export function useCreateKpiDefinition() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: KpiDefinitionInput) => {
      if (!profile) throw new Error('Not signed in.');
      const { data, error } = await supabase
        .from('kpi_definitions' as never)
        .insert({ ...input, organization_id: profile.organization_id } as never)
        .select('id')
        .single();
      if (error) throw error;
      return (data as unknown as { id: string }).id;
    },
    onSuccess: () => invalidate(queryClient),
  });
}

export function useUpdateKpiDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<KpiDefinitionInput>) => {
      const { id, ...patch } = input;
      const { error } = await supabase
        .from('kpi_definitions' as never)
        .update(patch as never)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => invalidate(queryClient),
  });
}

export function useDeleteKpiDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('kpi_definitions' as never).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => invalidate(queryClient),
  });
}

export function useRecordKpiValue() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { definitionId: string; asOf: string; value: number }) => {
      if (!profile) throw new Error('Not signed in.');
      const { error } = await supabase.from('kpi_snapshots' as never).upsert(
        {
          organization_id: profile.organization_id,
          kpi_definition_id: input.definitionId,
          as_of: input.asOf,
          value: input.value,
        } as never,
        { onConflict: 'kpi_definition_id,as_of' },
      );
      if (error) throw error;
    },
    onSuccess: () => invalidate(queryClient),
  });
}
